import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { adminService } from '../../services';
import { LoadingSpinner } from '../../components/ui/Loading';
import { getEventTypeTranslation } from '../../utils/eventTypeTranslation';
import { isFinanceRole, isAdminEquivalent } from '../../utils/permissions';

const AdminDashboard = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const [stats, setStats] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Carregar estatísticas do sistema
      const statsData = await adminService.getSystemStats();
      setStats(statsData);

      // Criar atividades recentes baseadas nas estatísticas
      const activities = [];

      // Adicionar atividade de usuários pendentes se houver
      if (statsData?.users?.pending > 0) {
        activities.push({
          id: 'pending-users',
          type: 'alistamento',
          message: `${statsData.users.pending} novo(s) alistamento(s) aguardando aprovação`,
          time: new Date().toISOString(),
          icon: '🎖️'
        });
      }

      // Adicionar atividade de usuários totais
      if (statsData?.users?.total) {
        activities.push({
          id: 'total-users',
          type: 'info',
          message: `${statsData.users.total} usuários ativos no sistema`,
          time: new Date().toISOString(),
          icon: '👥'
        });
      }

      // Adicionar atividade de personagens
      if (statsData?.characters?.total) {
        activities.push({
          id: 'total-characters',
          type: 'info',
          message: `${statsData.characters.total} personagens criados no clã`,
          time: new Date().toISOString(),
          icon: '⚔️'
        });
      }

      // Adicionar atividade de eventos ativos
      if (statsData?.events?.byStatus?.ACTIVE > 0) {
        activities.push({
          id: 'active-events',
          type: 'evento',
          message: `${statsData.events.byStatus.ACTIVE} evento(s) ativo(s) no momento`,
          time: new Date().toISOString(),
          icon: '🎯'
        });
      }

      // Adicionar atividade de eventos agendados
      if (statsData?.events?.byStatus?.SCHEDULED > 0) {
        activities.push({
          id: 'scheduled-events',
          type: 'evento',
          message: `${statsData.events.byStatus.SCHEDULED} evento(s) agendado(s)`,
          time: new Date().toISOString(),
          icon: '📅'
        });
      }

      // Adicionar atividade de nível médio
      if (statsData?.characters?.averageLevel) {
        activities.push({
          id: 'avg-level',
          type: 'info',
          message: `Nível médio do clã: ${Math.round(statsData.characters.averageLevel)}`,
          time: new Date().toISOString(),
          icon: '📊'
        });
      }

      // Adicionar atividade de pontos totais
      if (statsData?.characters?.totalPoints) {
        activities.push({
          id: 'total-points',
          type: 'gamificacao',
          message: `${formatNumber(statsData.characters.totalPoints)} pontos acumulados`,
          time: new Date().toISOString(),
          icon: '🏆'
        });
      }

      setRecentActivities(activities.slice(0, 8));
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Erro ao carregar dados do painel');
      setRecentActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('pt-BR').format(num || 0);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'agora';
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`;
    return `${Math.floor(diffInMinutes / 1440)}d atrás`;
  };

  const generateReport = () => {
    if (!stats) {
      notify.error('Dados não carregados ainda. Tente novamente.', '📊 Erro no Relatório');
      return;
    }

    const reportData = {
      timestamp: new Date().toLocaleString('pt-BR'),
      users: stats.users,
      characters: stats.characters,
      events: stats.events,
      items: stats.items,
      relics: stats.relics
    };

    const reportText = `
RELATÓRIO DO SISTEMA - ${reportData.timestamp}
===========================================

USUÁRIOS:
- Total: ${reportData.users?.total || 0}
- Administradores: ${reportData.users?.byRole?.ADMIN || 0}
- Membros: ${reportData.users?.byRole?.MEMBER || 0}

PERSONAGENS:
- Total: ${reportData.characters?.total || 0}
- Nível médio: ${Math.round(reportData.characters?.averageLevel || 0)}
- Pontos totais: ${formatNumber(reportData.characters?.totalPoints || 0)}

EVENTOS:
- Total: ${reportData.events?.total || 0}
- Ativos: ${reportData.events?.byStatus?.ACTIVE || 0}
- Agendados: ${reportData.events?.byStatus?.SCHEDULED || 0}

ITENS E RELÍQUIAS:
- Itens: ${reportData.items?.total || 0}
- Relíquias: ${reportData.relics?.total || 0}

Relatório gerado automaticamente pelo Sistema de Gerenciamento do Clã.
`;

    const blob = new Blob([reportText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-sistema-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    notify.success('Relatório gerado e baixado com sucesso!', '📊 Relatório Baixado');
  };

  // Check if user is admin
  if (!isAdminEquivalent(user?.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Acesso Negado
          </h2>
          <p className="text-gray-400 mb-6">
            Você não tem permissão para acessar o painel administrativo.
          </p>
          <button
            onClick={() => window.location.href = '/app/dashboard'}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
            ⚡ Painel Administrativo
          </h1>
          <p className="text-gray-300">
            Visão geral do sistema e ferramentas de gerenciamento
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg backdrop-blur-sm">
            {error}
          </div>
        )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Card 1 - Usuários */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-2xl">
                👥
              </div>
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <dt className="text-sm font-medium text-gray-400 truncate">
                Usuários Totais
              </dt>
              <dd className="text-2xl font-bold text-white mt-1">
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  formatNumber(stats?.users?.total)
                )}
              </dd>
            </div>
          </div>
          {stats?.users?.byRole && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400 flex justify-between">
              <span>Admins: {stats.users.byRole.ADMIN || 0}</span>
              <span>Membros: {stats.users.byRole.MEMBER || 0}</span>
            </div>
          )}
        </div>

        {/* Card 2 - Personagens */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-2xl">
                ⚔️
              </div>
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <dt className="text-sm font-medium text-gray-400 truncate">
                Personagens
              </dt>
              <dd className="text-2xl font-bold text-white mt-1">
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  formatNumber(stats?.characters?.total)
                )}
              </dd>
            </div>
          </div>
          {stats?.characters && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400">
              Nível médio: {Math.round(stats.characters.averageLevel || 0)}
            </div>
          )}
        </div>

        {/* Card 3 - Eventos */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center text-2xl">
                🎯
              </div>
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <dt className="text-sm font-medium text-gray-400 truncate">
                Eventos
              </dt>
              <dd className="text-2xl font-bold text-white mt-1">
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  formatNumber(stats?.events?.total)
                )}
              </dd>
            </div>
          </div>
          {stats?.events?.byStatus && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400 flex justify-between">
              <span>Agendados: {stats.events.byStatus.SCHEDULED || 0}</span>
              <span>Completos: {stats.events.byStatus.COMPLETED || 0}</span>
            </div>
          )}
        </div>

        {/* Card 4 - Pontos */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center text-2xl">
                🏆
              </div>
            </div>
            <div className="ml-4 flex-1 min-w-0">
              <dt className="text-sm font-medium text-gray-400 truncate">
                Pontos Totais
              </dt>
              <dd className="text-2xl font-bold text-white mt-1">
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  formatNumber(stats?.characters?.totalPoints)
                )}
              </dd>
            </div>
          </div>
          {stats?.items && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-gray-400 flex justify-between">
              <span>Itens: {stats.items.total || 0}</span>
              <span>Relíquias: {stats.relics?.total || 0}</span>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions & Recent Activities */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Ações Rápidas */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            ⚡ Ações Rápidas
          </h2>
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = '/app/admin/enlistments'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">🎖️</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Gerenciar Alistamentos
                </div>
                <div className="text-xs text-gray-400">Aprovar novos membros</div>
              </div>
              {stats?.users?.pending > 0 && (
                <span className="bg-red-500 text-white text-xs px-2.5 py-1 rounded-full font-bold">
                  {stats.users.pending}
                </span>
              )}
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/users'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">👥</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Gerenciar Usuários
                </div>
                <div className="text-xs text-gray-400">Editar e administrar usuários</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/items'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">⚔️</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Gerenciar Itens
                </div>
                <div className="text-xs text-gray-400">Criar e editar itens</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/relics'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">🏺</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Gerenciar Relíquias
                </div>
                <div className="text-xs text-gray-400">Criar e editar relíquias</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/blog'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">📰</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Gerenciar Blog
                </div>
                <div className="text-xs text-gray-400">Publique e edite posts do clã</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/notifications'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">🔔</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Notificações
                </div>
                <div className="text-xs text-gray-400">Envie push notifications para os membros</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/proof-review'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">📸</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Revisar Provas
                </div>
                <div className="text-xs text-gray-400">Analisar comprovantes de eventos</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/declined-events'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">🔒</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Ausências Informadas
                </div>
                <div className="text-xs text-gray-400">Ver justificativas de ausência</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/ranking'}
              className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all duration-300 text-left group"
            >
              <span className="text-2xl">🏆</span>
              <div className="flex-1">
                <div className="text-white font-medium group-hover:text-purple-400 transition-colors">
                  Rankings por Classe
                </div>
                <div className="text-xs text-gray-400">Ver top 3 de cada classe</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/special-events'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-orange-500/20"
            >
              <span className="text-2xl">??</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Eventos Especiais
                </div>
                <div className="text-xs text-orange-100">Grupos com drag-and-drop</div>
              </div>
            </button>

            {isFinanceRole(user?.role) && (
              <button
                onClick={() => window.location.href = '/app/admin/treasury'}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-yellow-600 to-amber-600 hover:from-yellow-500 hover:to-amber-500 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-yellow-500/20"
              >
                <span className="text-2xl">🏦</span>
                <div className="flex-1">
                  <div className="text-white font-medium">
                    Caixa do Clube
                  </div>
                  <div className="text-xs text-yellow-100">Extrato em Gold e em Real</div>
                </div>
              </button>
            )}

            {isFinanceRole(user?.role) && (
              <button
                onClick={() => window.location.href = '/app/admin/bc-payments/lookup'}
                className="w-full flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-yellow-500/50 rounded-xl transition-all duration-300 text-left group"
              >
                <span className="text-2xl">🔎</span>
                <div className="flex-1">
                  <div className="text-white font-medium group-hover:text-yellow-400 transition-colors">
                    Consulta de Pagamentos BC
                  </div>
                  <div className="text-xs text-gray-400">Veja o que já foi pago ou é devido a um personagem</div>
                </div>
              </button>
            )}

            <button
              onClick={() => window.location.href = '/app/admin/reports'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-cyan-500/20"
            >
              <span className="text-2xl">📊</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Relatórios Detalhados
                </div>
                <div className="text-xs text-cyan-100">Membros e eventos com filtros</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/reports/event-participation'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-500 hover:to-cyan-600 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-teal-500/20"
            >
              <span className="text-2xl">📋</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Participação em Eventos
                </div>
                <div className="text-xs text-teal-100">Confirmados por tipo de evento</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/reports/bc-participation'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-amber-500/20"
            >
              <span className="text-2xl">🛡️</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Participação nos Últimos 4 BC
                </div>
                <div className="text-xs text-amber-100">Quem participou e quem não</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/reports/clan-rotation-log'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-500 hover:to-green-600 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-emerald-500/20"
            >
              <span className="text-2xl">🔄</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Log de Rotatividade do Clã
                </div>
                <div className="text-xs text-emerald-100">Aceites e remoções da automação</div>
              </div>
            </button>

            <button
              onClick={() => window.location.href = '/app/admin/reports/access-log'}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-slate-500/20"
            >
              <span className="text-2xl">🛰️</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Log de Acesso
                </div>
                <div className="text-xs text-slate-200">Requisições feitas à API</div>
              </div>
            </button>

            <button
              onClick={() => generateReport()}
              className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-xl transition-all duration-300 text-left group shadow-lg shadow-purple-500/20"
            >
              <span className="text-2xl">🧾</span>
              <div className="flex-1">
                <div className="text-white font-medium">
                  Exportar Dados
                </div>
                <div className="text-xs text-purple-100">Baixar relatório completo</div>
              </div>
            </button>
          </div>
        </div>

        {/* Atividades Recentes */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            📝 Atividades Recentes
          </h2>

          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Carregando atividades..." />
            </div>
          ) : recentActivities.length > 0 ? (
            <div className="space-y-3">
              {recentActivities.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all duration-300"
                >
                  <span className="text-xl flex-shrink-0">{activity.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">
                      {activity.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {getTimeAgo(activity.time)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <span className="text-4xl mb-3 block">📝</span>
              <p className="text-sm">
                Nenhuma atividade administrativa recente
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reports Section */}
      <div className="mt-8">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            📊 Relatórios Rápidos
          </h2>

          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : stats ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Usuários por Função */}
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border border-blue-500/20 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">👥</span>
                  </div>
                  <h3 className="font-semibold text-blue-400">Usuários por Função</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Administradores:</span>
                    <span className="font-medium text-white">{stats.users?.byRole?.ADMIN || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Membros:</span>
                    <span className="font-medium text-white">{stats.users?.byRole?.MEMBER || 0}</span>
                  </div>
                </div>
              </div>

              {/* Personagens por Classe */}
              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 border border-green-500/20 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">⚔️</span>
                  </div>
                  <h3 className="font-semibold text-green-400">Personagens por Classe</h3>
                </div>
                <div className="space-y-3 text-sm">
                  {stats.characters?.byClassAndStatus && Object.entries(stats.characters.byClassAndStatus).sort().map(([className, counts]) => (
                    <div key={className} className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300 font-medium">{className}:</span>
                        <span className="font-semibold text-white">{counts.total}</span>
                      </div>
                      <div className="flex justify-between items-center pl-4 text-xs">
                        <span className="text-green-400">✓ Ativos:</span>
                        <span className="text-green-400">{counts.ACTIVE || 0}</span>
                      </div>
                      <div className="flex justify-between items-center pl-4 text-xs">
                        <span className="text-red-400">✗ Inativos:</span>
                        <span className="text-red-400">{counts.INACTIVE || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Eventos por Tipo */}
              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-xl">🎯</span>
                  </div>
                  <h3 className="font-semibold text-purple-400">Eventos por Tipo</h3>
                </div>
                <div className="space-y-2 text-sm">
                  {stats.events?.byType && Object.entries(stats.events.byType).slice(0, 3).map(([eventType, count]) => (
                    <div key={eventType} className="flex justify-between items-center">
                      <span className="text-gray-400">{getEventTypeTranslation(eventType)}:</span>
                      <span className="font-medium text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              <span className="text-4xl mb-3 block">📊</span>
              <p className="text-sm">
                Dados de relatórios não disponíveis
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminDashboard;